import React, { useCallback, useState, useEffect, useMemo } from 'react'
import PaginatedPage from '../../components/PaginatedPage'
import NodePresenter from '../../api/nodePresenter'
import { Tree, Modal, Input, Row, useWindowContext, Checkbox } from '@iqueue/ui-kit'
import { centroid } from '@turf/turf'

import usePermissions from '../../components/usePermissions'
import PickNodeType from '../../components/picker/PickNodeType'
import PickNode from '../../components/picker/PickNode'
import PickPolygon from '../../components/picker/PickPolygon'
import ViewStatusNodes from '../../components/ViewStatusNodes'
import useTranslate, { t } from '../../hooks/useTranslate'
import PickLocation from '../../components/picker/PickLocation'

const childForType = {
  ORGANIZATION: 'FACTORY',
  FACTORY: 'FIELD',
  FIELD: 'COLLECTION_POINT',
  COLLECTION_POINT: 'WELL',
}

function NodeModal({ node, onApply, onClose, isCreateInside, isUpdateInside, currentNode, onChecked }) {
  const [type, setType] = useState('')
  const [geoJSON, setGeoJSON] = useState([])

  useEffect(() => {
    setType(currentNode?.type)
    onChecked(currentNode?.hasChirpstackInstance || false)
  }, [currentNode])

  return <Modal
    fullscreen
    onApply={ onApply }
    isOpened={ !!node }
    onClose={ onClose }
    title={ isCreateInside && `${ t('nodes.nodeInside') }: ${ currentNode?.name }` || isUpdateInside && `${ t('nodes.updateNodeInside') }: ${ currentNode?.name }` }
    footerActions={ [
      { key: 'cancel', title: t('nodes.close'), danger: true },
      {
        key: 'apply',
        title: isCreateInside ? t('nodes.create') : isUpdateInside ? t('nodes.update') : '',
        submit: true,
        primary: true,
      },
    ] }
  >
    <Input name={ 'id' } hidden value={ currentNode?.id }/>
    <Row>
      <Input
        autoFocus
        required
        size={ 12 }
        name={ 'name' }
        placeholder={ t('nodes.name') }
        value={ !isCreateInside ? currentNode?.name : '' }
      />
    </Row>

    { isUpdateInside && <Row>
      <Checkbox
        title={ t('nodes.chirpstackInstalled') }
        name={ 'hasChirpstackInstance' }
        checked={ currentNode?.meta?.hasChirpstackInstance }
        onChange={ (item) => {
          onChecked(item)
        } }
      />
    </Row> }

    <Row>
      <PickNodeType
        required
        size={ 12 }
        name={ 'type' }
        placeholder={ t('nodes.type') }
        value={ type }
        onChange={ setType }
        isCreateInside={ isCreateInside }
      />
    </Row>

    <Row>
      <PickNode
        hidden={ isCreateInside || isUpdateInside }
        size={ 12 }
        name={ 'parentId' }
        current={ currentNode }
        placeholder={ t('nodes.underControl') }
        value={ currentNode?.type === 'ORGANIZATION' ? currentNode?.id : currentNode?.parentId }
        isCreateInside={ isUpdateInside }
      />
    </Row>

    { !isCreateInside && (
      <Row>
        <Input
          size={ 12 }
          type={ 'text' }
          name={ 'host' }
          placeholder={ t('nodes.serverIp') }
          value={ currentNode?.host }
        />
      </Row>
    )
    }

    { type === 'WELL' && isUpdateInside && <>
      <Row>
        <Input
          placeholder={ t('nodes.plumeLength') }
          name={ 'plumeLength' }
          type={ 'number' }
          value={ currentNode?.meta?.wellTechnicalInformation?.plumeLength }
          required
          size={ 4 }
        />

        <Input
          placeholder={ t('nodes.pipeThickness') }
          name={ 'pipeThickness' }
          type={ 'number' }
          value={ currentNode?.meta?.wellTechnicalInformation?.pipeThickness }
          required
          size={ 4 }
        />

        <Input
          placeholder={ t('nodes.plumeDiameter') }
          name={ 'plumeDiameter' }
          value={ currentNode?.meta?.wellTechnicalInformation?.plumeDiameter }
          type={ 'number' }
          required
          size={ 4 }
        />
      </Row>
    </> }

    <>
      { type === 'FIELD' ? <>
        <PickPolygon
          nodes={ currentNode }
          name={ 'geoJson' }
          value={ currentNode?.geoJson }
          onChange={ setGeoJSON }
        />
      </> : <PickLocation
        name={ 'location' }
        value={ currentNode?.location }
      /> }
    </>
  </Modal>
}

function NodesTable() {
  const [nodes, setNodes] = useState([])
  const [currentNode, setCurrentNode] = useState(null)
  const [openNodeModal, setOpenNodeModal] = useState(false)
  const [isCreateInside, setIsCreateInside] = useState(false)
  const [isUpdateInside, setUpdateInside] = useState(false)
  const [openedPoligon, setOpenedPoligon] = useState(false)
  const [checked, setChecked] = useState(false)
  const { permissions } = usePermissions()

  const handleChangeNode = useCallback(async () => {
    await NodePresenter.tree({ depth: 500 }).then(setNodes)
  }, [])

  const hasPermission = useCallback((perm) => {
    return permissions[NodePresenter.key.toUpperCase()]?.includes(perm)
  }, [])

  const buildTree = useCallback((nodes, parentId = null) => {
    function leaf(nodes) {
      return nodes.map(node => {
        const actions = []
        if (hasPermission('WRITE') && node.type !== 'WELL') {
          actions.push({
            key: 'add',
            icon: 'add',
            primary: true,
            onClick: () => {
              setCurrentNode({
                parentId: node.parentId,
                type: childForType[node.type],
                name: node.name,
                id: node.id,
                geoJson: node.geoJson,
              })
              setIsCreateInside(true)
              setOpenNodeModal(true)
            },
          })
        }

        if (hasPermission('DELETE')) {
          actions.push({
            key: 'delete',
            icon: 'delete',
            danger: true,
            onClick: async () => {
              await NodePresenter.delete(node.id)
              await handleChangeNode()
            },
          })
        }

        return {
          key: node.id,
          expanded: true,
          status: node.status,
          type: node.type,
          ...node,
          children: leaf(node.children, node.id),
          actions,
        }
      })
    }

    return leaf(nodes, parentId)
  }, [hasPermission])

  const handleSubmitModal = useCallback(async (item) => {
    const {
      id,
      name,
      geoJson,
      parentId,
      type,
      location,
      pipeThickness,
      plumeDiameter,
      plumeLength,
      host
    } = item

    const payload = {
      parentId: isCreateInside ? id : parentId,
      location,
      name,
      type,
      host

    }

    const updateMeta = {
      hasChirpstackInstance: checked,
    }

    if (geoJson) {
      const center = centroid(geoJson)
      const [lng, lat] = center.geometry.coordinates
      payload.location = { lat, lng }
      payload.geoJson = geoJson
    } else {
      payload.geoJson = {}
    }

    if (type === 'WELL') {
      updateMeta.wellTechnicalInformation = {
        pipeThickness: pipeThickness,
        plumeDiameter: plumeDiameter,
        plumeLength: plumeLength,
      }
    }

    if (isCreateInside) {
      await NodePresenter.post('', payload)
    }

    if (isUpdateInside) {
      await NodePresenter.patch(`${ id }`, payload)
      await NodePresenter.patch(`update-meta/${ id }`, updateMeta)
    }

    setChecked(false)

    await handleChangeNode()
  }, [isCreateInside, isUpdateInside, checked])

  useEffect(() => {
    handleChangeNode().catch((err) => console.error(err))
  }, [])

  return <div className={ 'tree-main' }>
    <Tree
      key={ nodes.length }
      selectMode={ 'none' }
      entries={ buildTree(nodes) }
      onItemClick={ (v) => {
        setCurrentNode(v)
        if (hasPermission('UPDATE')) {
          setOpenNodeModal(true)
        }
        setUpdateInside(true)
      } }
      render={ (v) => {
        return <div className={ 'node-tree' } key={ v.id }>
          <div>
            <b>{ v.name }</b>
            <span>
              &nbsp;
              <ViewStatusNodes item={ v }/>
            </span>
            <div>{ t(`type.node.${ v.type }`) }</div>
          </div>
        </div>
      } }
    />

    <NodeModal
      node={ openNodeModal }
      onApply={ (item) => handleSubmitModal(item) }
      onClose={ () => {
        setOpenNodeModal(false)
        setCurrentNode(null)
        setUpdateInside(false)
        setIsCreateInside(false)
        setChecked(false)
      } }
      isCreateInside={ isCreateInside }
      isUpdateInside={ isUpdateInside }
      currentNode={ currentNode }
      onChecked={ setChecked }
    />

    <Modal
      isOpened={ openedPoligon }
      onClose={ () => {
        setCurrentNode(null)
        setOpenedPoligon(false)
      } }
      title={ `${ currentNode?.name }` }
      fullscreen
      onApply={ async (item) => {
        await NodePresenter.patch(`${ currentNode.id }`, {
          geoJson: item?.geoJson,
        })
        await handleChangeNode()

      } }
      footerActions={ [
        { key: 'cancel', title: t('nodes.close'), danger: true },
        { key: 'apply', title: t('nodes.apply'), submit: true, primary: true },
      ] }
    >
    </Modal>
  </div>
}

export default function NodesPage() {
  const { togglePaddings, setTitle, toggleScrollbars } = useWindowContext()
  const locale = useTranslate()
  const translatedTitle = useMemo(() => t('menu.nodes'), [locale])

  useEffect(() => {
    setTitle(translatedTitle)
    togglePaddings(false)
    toggleScrollbars(true)
  }, [translatedTitle])

  return <PaginatedPage
    presenter={ NodePresenter }
    component={ NodesTable }
  />
}
